import type { DrawingCoordinate } from './geometry';
import type {
	DrawingMode,
	UseMapDrawingOptions,
	UseMapDrawingReturn
} from './types';

import {
	createRectanglePath,
	getClustersInsideShape,
	projectCoordinateToPixel
} from './geometry';

const HOVER_DEBOUNCE_MS = 60;
const HOVER_MAX_WAIT_MS = 120;
const MIN_FREEHAND_POINT_DISTANCE_PX = 3;

const SHAPE_STYLE = {
	strokeColor: '#1a1a1a',
	strokeOpacity: 0.9,
	strokeWeight: 2,
	fillColor: '#1a1a1a',
	fillOpacity: 0.15
};

const POLYLINE_STYLE = {
	strokeColor: '#1a1a1a',
	strokeOpacity: 0.9,
	strokeWeight: 2
};

/**
 * Manages freehand/rectangle drawing and cluster hit-testing on Google Maps.
 * Pointer Events are used so the same interaction works with mouse, pen and
 * touch, including when the pointer is released outside the map.
 */
export function useMapDrawing({
	gmapRef,
	clusters,
	onClustersSelected,
	onHoveredClustersChanged
}: UseMapDrawingOptions): UseMapDrawingReturn {
	const isDrawingMode = ref(false);
	const isDrawing = ref(false);
	const currentDrawingMode = ref<DrawingMode>('freehand');
	// Google Maps MVCObjects keep private mutable state and must retain their
	// original identity. A deep Vue proxy can detach the visible overlay from the
	// instance on which setMap(null) is called, making it reappear after a redraw.
	const drawnPolygon = shallowRef<google.maps.Polygon | null>(null);
	const drawnRectangle = shallowRef<google.maps.Rectangle | null>(null);
	const drawingPath = ref<google.maps.LatLng[]>([]);
	const rectangleStartPoint = ref<google.maps.LatLng | null>(null);
	const rectangleEndPoint = ref<google.maps.LatLng | null>(null);

	let polyline: google.maps.Polyline | null = null;
	let previewRectangle: google.maps.Rectangle | null = null;
	let activeMap: google.maps.Map | null = null;
	let activeMapDiv: HTMLElement | null = null;
	let activePointerId: number | null = null;
	let hasActiveWindowListeners = false;
	let previousCursor = '';
	let previousTouchAction = '';
	let previousMapOptions: google.maps.MapOptions | null = null;
	let hoverDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	let hoverMaxWaitTimer: ReturnType<typeof setTimeout> | null = null;
	let pendingHoverPath: DrawingCoordinate[] | null = null;
	let hoveredClusterIds = new Set<string>();

	function getMapInstance(): google.maps.Map | null {
		return gmapRef.value?.mapInstance || null;
	}

	function getMapOption<T>(map: google.maps.Map, key: string, fallback: T): T {
		const mapWithGetter = map as google.maps.Map & {
			get?: (propertyName: string) => unknown;
		};
		const value = mapWithGetter.get?.(key);
		return (value ?? fallback) as T;
	}

	function disableMapInteraction(map: google.maps.Map): void {
		activeMap = map;
		previousMapOptions = {
			draggable: getMapOption(map, 'draggable', true),
			scrollwheel: getMapOption(map, 'scrollwheel', true),
			disableDoubleClickZoom: getMapOption(
				map,
				'disableDoubleClickZoom',
				false
			),
			gestureHandling: getMapOption(map, 'gestureHandling', 'greedy')
		};

		map.setOptions({
			draggable: false,
			scrollwheel: false,
			disableDoubleClickZoom: true,
			gestureHandling: 'none'
		});

		activeMapDiv = map.getDiv();
		previousCursor = activeMapDiv.style.cursor;
		previousTouchAction = activeMapDiv.style.touchAction;
		activeMapDiv.style.cursor = 'crosshair';
		activeMapDiv.style.touchAction = 'none';
	}

	function restoreMapInteraction(): void {
		if (activeMap && previousMapOptions) {
			activeMap.setOptions(previousMapOptions);
		}

		if (activeMapDiv) {
			activeMapDiv.style.cursor = previousCursor;
			activeMapDiv.style.touchAction = previousTouchAction;
		}

		previousMapOptions = null;
		activeMap = null;
		activeMapDiv = null;
		previousCursor = '';
		previousTouchAction = '';
	}

	function setsAreEqual(first: Set<string>, second: Set<string>): boolean {
		return (
			first.size === second.size &&
			Array.from(first).every(clusterId => second.has(clusterId))
		);
	}

	function emitHoveredClusters(clusterIds: string[]): void {
		const nextIds = new Set(clusterIds);
		if (setsAreEqual(hoveredClusterIds, nextIds)) return;

		hoveredClusterIds = nextIds;
		onHoveredClustersChanged?.(clusterIds);
	}

	function cancelHoverComputation(): void {
		if (hoverDebounceTimer) {
			clearTimeout(hoverDebounceTimer);
			hoverDebounceTimer = null;
		}
		if (hoverMaxWaitTimer) {
			clearTimeout(hoverMaxWaitTimer);
			hoverMaxWaitTimer = null;
		}
		pendingHoverPath = null;
	}

	function clearHoveredClusters(): void {
		cancelHoverComputation();
		emitHoveredClusters([]);
	}

	function getClustersForPath(path: DrawingCoordinate[]): string[] {
		const zoom = activeMap?.getZoom() ?? getMapInstance()?.getZoom() ?? 0;
		return getClustersInsideShape(clusters.value, path, zoom).map(
			cluster => cluster.geometry.id
		);
	}

	function flushHoverComputation(): void {
		const path = pendingHoverPath;
		cancelHoverComputation();
		if (!path) return;

		emitHoveredClusters(getClustersForPath(path));
	}

	function scheduleHoverComputation(path: DrawingCoordinate[]): void {
		if (path.length < 3) return;

		pendingHoverPath = path.map(coordinate => ({ ...coordinate }));

		if (hoverDebounceTimer) clearTimeout(hoverDebounceTimer);
		hoverDebounceTimer = setTimeout(flushHoverComputation, HOVER_DEBOUNCE_MS);

		if (!hoverMaxWaitTimer) {
			hoverMaxWaitTimer = setTimeout(flushHoverComputation, HOVER_MAX_WAIT_MS);
		}
	}

	function clearPolyline(): void {
		polyline?.setMap(null);
		polyline = null;
	}

	function clearPreviewRectangle(): void {
		previewRectangle?.setMap(null);
		previewRectangle = null;
	}

	function clearDrawnPolygon(): void {
		drawnPolygon.value?.setMap(null);
		drawnPolygon.value = null;
	}

	function clearDrawnRectangle(): void {
		drawnRectangle.value?.setMap(null);
		drawnRectangle.value = null;
	}

	function clearDrawnShapes(): void {
		clearDrawnPolygon();
		clearDrawnRectangle();
		clearHoveredClusters();
	}

	function createPolyline(map: google.maps.Map): google.maps.Polyline {
		return new google.maps.Polyline({
			map,
			...POLYLINE_STYLE,
			clickable: false
		});
	}

	function createPolygon(
		map: google.maps.Map,
		path: google.maps.LatLng[]
	): google.maps.Polygon {
		return new google.maps.Polygon({
			map,
			paths: path,
			...SHAPE_STYLE,
			clickable: false
		});
	}

	function createBoundsFromPoints(
		point1: google.maps.LatLng,
		point2: google.maps.LatLng
	): google.maps.LatLngBounds {
		const bounds = new google.maps.LatLngBounds();
		bounds.extend(point1);
		bounds.extend(point2);
		return bounds;
	}

	function createRectangle(
		map: google.maps.Map,
		bounds: google.maps.LatLngBounds
	): google.maps.Rectangle {
		return new google.maps.Rectangle({
			map,
			bounds,
			...SHAPE_STYLE,
			clickable: false
		});
	}

	function toCoordinate(latLng: google.maps.LatLng): DrawingCoordinate {
		return { lat: latLng.lat(), lng: latLng.lng() };
	}

	function getFreehandPath(): DrawingCoordinate[] {
		return drawingPath.value.map(toCoordinate);
	}

	function getRectanglePath(): DrawingCoordinate[] {
		if (!rectangleStartPoint.value || !rectangleEndPoint.value) return [];

		return createRectanglePath(
			toCoordinate(rectangleStartPoint.value),
			toCoordinate(rectangleEndPoint.value)
		);
	}

	function beginFreehandDrawing(latLng: google.maps.LatLng): void {
		const map = activeMap;
		if (!map) return;

		isDrawing.value = true;
		clearDrawnShapes();
		drawingPath.value = [latLng];
		polyline = createPolyline(map);
		polyline.getPath().push(latLng);
	}

	function updateFreehandDrawing(latLng: google.maps.LatLng): void {
		if (!isDrawing.value || !polyline) return;

		const lastPoint = drawingPath.value.at(-1);
		if (lastPoint?.equals?.(latLng)) return;
		const zoom = activeMap?.getZoom();
		if (lastPoint && zoom !== undefined) {
			const previousPixel = projectCoordinateToPixel(
				toCoordinate(lastPoint),
				zoom
			);
			const nextPixel = projectCoordinateToPixel(toCoordinate(latLng), zoom);
			if (
				Math.hypot(
					nextPixel.x - previousPixel.x,
					nextPixel.y - previousPixel.y
				) < MIN_FREEHAND_POINT_DISTANCE_PX
			) {
				return;
			}
		}

		drawingPath.value.push(latLng);
		polyline.getPath().push(latLng);
		scheduleHoverComputation(getFreehandPath());
	}

	function finishFreehandDrawing(): void {
		if (!isDrawing.value) return;
		isDrawing.value = false;

		const map = activeMap;
		const path = getFreehandPath();
		clearPolyline();
		cancelHoverComputation();

		if (!map || path.length < 3) {
			drawingPath.value = [];
			clearHoveredClusters();
			return;
		}

		drawnPolygon.value = createPolygon(map, drawingPath.value);
		const clusterIds = getClustersForPath(path);

		if (clusterIds.length === 0) {
			clearDrawnPolygon();
			drawingPath.value = [];
			clearHoveredClusters();
			return;
		}

		if (onClustersSelected) onClustersSelected(clusterIds);
		else clearDrawnPolygon();
		stopDrawingMode();
	}

	function beginRectangleDrawing(latLng: google.maps.LatLng): void {
		if (!activeMap) return;

		isDrawing.value = true;
		clearDrawnShapes();
		rectangleStartPoint.value = latLng;
		rectangleEndPoint.value = latLng;
	}

	function updateRectangleDrawing(latLng: google.maps.LatLng): void {
		if (!isDrawing.value || !rectangleStartPoint.value || !activeMap) return;

		rectangleEndPoint.value = latLng;
		const bounds = createBoundsFromPoints(rectangleStartPoint.value, latLng);

		if (previewRectangle) {
			previewRectangle.setBounds(bounds);
		} else {
			previewRectangle = new google.maps.Rectangle({
				map: activeMap,
				bounds,
				...SHAPE_STYLE,
				fillOpacity: 0.1,
				clickable: false
			});
		}

		scheduleHoverComputation(getRectanglePath());
	}

	function finishRectangleDrawing(): void {
		if (!isDrawing.value || !rectangleStartPoint.value) return;
		isDrawing.value = false;

		const map = activeMap;
		const path = getRectanglePath();
		clearPreviewRectangle();
		cancelHoverComputation();

		if (!map || !rectangleEndPoint.value || path.length < 3) {
			rectangleStartPoint.value = null;
			rectangleEndPoint.value = null;
			clearHoveredClusters();
			return;
		}

		const bounds = createBoundsFromPoints(
			rectangleStartPoint.value,
			rectangleEndPoint.value
		);
		drawnRectangle.value = createRectangle(map, bounds);
		const clusterIds = getClustersForPath(path);

		if (clusterIds.length === 0) {
			clearDrawnRectangle();
			rectangleStartPoint.value = null;
			rectangleEndPoint.value = null;
			clearHoveredClusters();
			return;
		}

		if (onClustersSelected) onClustersSelected(clusterIds);
		else clearDrawnRectangle();
		stopDrawingMode();
	}

	function isMapControl(target: EventTarget | null): boolean {
		return (
			target instanceof Element &&
			Boolean(
				target.closest('button, a, input, select, textarea, [role="button"]')
			)
		);
	}

	function pointerEventToLatLng(
		event: PointerEvent
	): google.maps.LatLng | null {
		if (!activeMap || !activeMapDiv) return null;

		const projection = activeMap.getProjection();
		const bounds = activeMap.getBounds();
		const zoom = activeMap.getZoom();
		if (!projection || !bounds || zoom === undefined) return null;

		const northEast = projection.fromLatLngToPoint(bounds.getNorthEast());
		const southWest = projection.fromLatLngToPoint(bounds.getSouthWest());
		if (!northEast || !southWest) return null;

		const rect = activeMapDiv.getBoundingClientRect();
		const scale = 2 ** zoom;
		const worldPoint = new google.maps.Point(
			southWest.x + (event.clientX - rect.left) / scale,
			northEast.y + (event.clientY - rect.top) / scale
		);

		return projection.fromPointToLatLng(worldPoint);
	}

	function consumePointerEvent(event: PointerEvent): void {
		event.preventDefault();
		event.stopPropagation();
	}

	function handlePointerDown(event: PointerEvent): void {
		if (
			!isDrawingMode.value ||
			!event.isPrimary ||
			event.button !== 0 ||
			activePointerId !== null ||
			isMapControl(event.target)
		) {
			return;
		}

		const latLng = pointerEventToLatLng(event);
		if (!latLng || !activeMapDiv) return;

		consumePointerEvent(event);
		activePointerId = event.pointerId;
		addActiveWindowListeners();
		try {
			activeMapDiv.setPointerCapture?.(event.pointerId);
		} catch {
			// Synthetic events and older browsers may not expose an active pointer.
		}

		if (currentDrawingMode.value === 'freehand') {
			beginFreehandDrawing(latLng);
		} else {
			beginRectangleDrawing(latLng);
		}
	}

	function handlePointerMove(event: PointerEvent): void {
		if (event.pointerId !== activePointerId || !isDrawing.value) return;

		const latLng = pointerEventToLatLng(event);
		if (!latLng) return;

		consumePointerEvent(event);
		if (currentDrawingMode.value === 'freehand') {
			updateFreehandDrawing(latLng);
		} else {
			updateRectangleDrawing(latLng);
		}
	}

	function handlePointerUp(event: PointerEvent): void {
		if (event.pointerId !== activePointerId) return;

		const latLng = pointerEventToLatLng(event);
		consumePointerEvent(event);

		if (latLng) {
			if (currentDrawingMode.value === 'freehand') {
				updateFreehandDrawing(latLng);
			} else {
				updateRectangleDrawing(latLng);
			}
		}

		if (currentDrawingMode.value === 'freehand') {
			finishFreehandDrawing();
		} else {
			finishRectangleDrawing();
		}

		releaseActivePointer();
	}

	function handlePointerCancel(event: PointerEvent): void {
		if (event.pointerId !== activePointerId) return;
		consumePointerEvent(event);
		stopDrawingMode();
	}

	function handleWindowBlur(): void {
		if (activePointerId === null) return;
		stopDrawingMode();
	}

	function addActiveWindowListeners(): void {
		if (hasActiveWindowListeners) return;

		window.addEventListener('pointermove', handlePointerMove, {
			capture: true,
			passive: false
		});
		window.addEventListener('pointerup', handlePointerUp, {
			capture: true,
			passive: false
		});
		window.addEventListener('pointercancel', handlePointerCancel, {
			capture: true,
			passive: false
		});
		window.addEventListener('blur', handleWindowBlur, true);
		hasActiveWindowListeners = true;
	}

	function removeActiveWindowListeners(): void {
		if (!hasActiveWindowListeners) return;

		window.removeEventListener('pointermove', handlePointerMove, true);
		window.removeEventListener('pointerup', handlePointerUp, true);
		window.removeEventListener('pointercancel', handlePointerCancel, true);
		window.removeEventListener('blur', handleWindowBlur, true);
		hasActiveWindowListeners = false;
	}

	function releaseActivePointer(): void {
		if (activeMapDiv && activePointerId !== null) {
			try {
				if (activeMapDiv.hasPointerCapture?.(activePointerId)) {
					activeMapDiv.releasePointerCapture?.(activePointerId);
				}
			} catch {
				// The browser may have released capture before pointerup/cancel.
			}
		}

		removeActiveWindowListeners();
		activePointerId = null;
	}

	function addPointerListeners(mapDiv: HTMLElement): void {
		mapDiv.addEventListener('pointerdown', handlePointerDown, {
			capture: true,
			passive: false
		});
	}

	function removePointerListeners(): void {
		releaseActivePointer();
		activeMapDiv?.removeEventListener('pointerdown', handlePointerDown, true);
	}

	function startDrawingMode(mode?: DrawingMode): void {
		const map = getMapInstance();
		if (!map) return;

		if (isDrawingMode.value || activeMap) {
			stopDrawingMode();
		}
		if (mode) currentDrawingMode.value = mode;

		isDrawingMode.value = true;
		clearDrawnShapes();
		disableMapInteraction(map);
		if (activeMapDiv) addPointerListeners(activeMapDiv);
	}

	function stopDrawingMode(): void {
		isDrawingMode.value = false;
		isDrawing.value = false;

		removePointerListeners();
		clearPolyline();
		clearPreviewRectangle();
		clearHoveredClusters();
		drawingPath.value = [];
		rectangleStartPoint.value = null;
		rectangleEndPoint.value = null;
		restoreMapInteraction();
	}

	function resetDrawing(): void {
		stopDrawingMode();
		clearDrawnShapes();
	}

	function toggleDrawingMode(mode?: DrawingMode): void {
		const requestedMode = mode || 'freehand';

		if (!isDrawingMode.value) {
			startDrawingMode(requestedMode);
			return;
		}

		if (currentDrawingMode.value === requestedMode) {
			stopDrawingMode();
		} else {
			startDrawingMode(requestedMode);
		}
	}

	function setDrawingMode(mode: DrawingMode): void {
		currentDrawingMode.value = mode;
		if (isDrawingMode.value) startDrawingMode(mode);
	}

	onScopeDispose(resetDrawing);

	return {
		isDrawingMode: readonly(isDrawingMode),
		isDrawing: readonly(isDrawing),
		currentDrawingMode: readonly(currentDrawingMode),
		drawnPolygon: shallowReadonly(drawnPolygon),
		drawnRectangle: shallowReadonly(drawnRectangle),
		startDrawingMode,
		stopDrawingMode,
		toggleDrawingMode,
		setDrawingMode,
		clearDrawnShapes,
		resetDrawing
	};
}
